import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';

const CameraMovingContext = createContext(false);

interface OrbitControlsLike {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

interface CameraMovingProviderProps {
  controlsRef: MutableRefObject<OrbitControlsLike | null>;
  children: ReactNode;
}

/**
 * Tracks OrbitControls interaction so point cloud renderers can downsample
 * while the camera is moving.
 */
export const CameraMovingProvider: React.FC<CameraMovingProviderProps> = ({
  controlsRef,
  children,
}) => {
  const [isCameraMoving, setIsCameraMoving] = useState(false);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let rafId = 0;

    const attachListeners = () => {
      const controls = controlsRef.current;
      if (!controls) {
        return false;
      }

      const onStart = () => setIsCameraMoving(true);
      const onEnd = () => setIsCameraMoving(false);

      controls.addEventListener('start', onStart);
      controls.addEventListener('end', onEnd);

      cleanup = () => {
        controls.removeEventListener('start', onStart);
        controls.removeEventListener('end', onEnd);
      };
      return true;
    };

    if (!attachListeners()) {
      rafId = requestAnimationFrame(() => {
        attachListeners();
      });
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      cleanup?.();
    };
  }, [controlsRef]);

  return React.createElement(
    CameraMovingContext.Provider,
    { value: isCameraMoving },
    children
  );
};

export const useCameraMoving = (): boolean => useContext(CameraMovingContext);
